import type { TFunction } from 'i18next';

import type {
  PortalBillingPeriod,
  PortalPlanCtaType,
  PortalSubscriptionPlan,
} from '../types/portalPlans';
import { formatMoney } from '@/shared/utils/money';

export const FREE_TRIAL_PLAN_CODE = 'free_trial';

export function isPortalBillingPeriod(value: string): value is PortalBillingPeriod {
  return value === 'monthly' || value === 'yearly';
}

export function filterPlansByBillingCycle(
  plans: PortalSubscriptionPlan[],
  billingCycle: PortalBillingPeriod,
): PortalSubscriptionPlan[] {
  return plans
    .filter((plan) => plan.billing_cycle === billingCycle)
    .sort((a, b) => a.sort_order - b.sort_order || a.name.localeCompare(b.name));
}

export function resolvePortalPlanDisplayName(plan: PortalSubscriptionPlan, locale: string): string {
  if (locale.startsWith('ar') && plan.name_ar) {
    return plan.name_ar;
  }
  return plan.name;
}

export function formatPortalPlanPrice(
  plan: Pick<PortalSubscriptionPlan, 'price_amount' | 'currency'>,
  locale: string,
  displayCurrency?: string,
): string {
  const language = locale.startsWith('ar') ? 'ar' : 'en';
  return formatMoney(plan.price_amount, displayCurrency ?? plan.currency, language);
}

const AR_PLAN_DESCRIPTIONS: Record<string, string> = {
  free_trial: 'ابدأ تجربة وارد بدون التزام وتعرّف على النظام قبل الاشتراك.',
  basic: 'مناسبة للبدايات والمتاجر الصغيرة التي تحتاج أساسيات البيع والإدارة.',
  advanced: 'مناسبة للشركات النامية التي تحتاج فروع ومستخدمين وتقارير أكثر.',
  professional: 'أفضل اختيار للشركات التي تحتاج تشغيل متكامل وحدود أعلى.',
  enterprise: 'حل متقدم للشركات الكبيرة والاحتياجات الخاصة.',
};

const FEATURE_LABELS_AR: Record<string, string> = {
  pos: 'نقاط البيع',
  sales: 'المبيعات',
  inventory: 'المخزون',
  reports: 'التقارير',
  dashboard: 'لوحة التحكم',
  settings: 'الإعدادات',
  purchasing: 'المشتريات',
  finance: 'المالية',
  audit: 'سجل العمليات',
  ai: 'الذكاء الاصطناعي',
  offline_sync: 'المزامنة بدون إنترنت',
  users: 'إدارة المستخدمين',
  branches: 'إدارة الفروع',
  products: 'إدارة المنتجات',
  warehouses: 'إدارة المخازن',
};

export function resolvePortalPlanDescription(
  plan: PortalSubscriptionPlan,
  locale: string,
): string | null {
  if (locale.startsWith('ar')) {
    return AR_PLAN_DESCRIPTIONS[plan.plan_code] ?? AR_PLAN_DESCRIPTIONS[plan.slug] ?? null;
  }
  return plan.description;
}

export function translatePortalPlanFeatureLine(feature: string, locale: string): string {
  if (!locale.startsWith('ar')) return feature;
  const key = feature.trim().toLowerCase();
  return FEATURE_LABELS_AR[key] ?? feature.replace(/_/g, ' ');
}

export function isFreeTrialPlan(plan: PortalSubscriptionPlan): boolean {
  return plan.plan_code === FREE_TRIAL_PLAN_CODE;
}

export function resolvePortalPlanCtaLabelKey(ctaType: PortalPlanCtaType): string {
  return `portal.plans.cta.${ctaType}`;
}

export function isPortalUpgradeCtaEnabled(
  plan: Pick<PortalSubscriptionPlan, 'cta_enabled' | 'cta_type'>,
): boolean {
  return plan.cta_enabled && plan.cta_type === 'upgrade';
}

export function resolvePortalPlanDisabledReasonKey(
  reason: string | null | undefined,
): string | null {
  if (!reason) return null;
  return `portal.plans.disabledReason.${reason}`;
}

export function translatePortalPlanDisabledReason(
  reason: string | null | undefined,
  t: TFunction,
): string | null {
  const key = resolvePortalPlanDisabledReasonKey(reason);
  if (!key) return null;
  return t(key, { defaultValue: reason ?? undefined });
}

export function extractPortalPlanFeatureLines(features: unknown): string[] {
  if (!features) return [];

  if (Array.isArray(features)) {
    return features.filter((item): item is string => typeof item === 'string' && item.length > 0);
  }

  if (typeof features === 'object') {
    return Object.entries(features as Record<string, unknown>)
      .filter(([, value]) => value === true || (typeof value === 'string' && value.length > 0))
      .map(([key, value]) => (typeof value === 'string' ? value : key));
  }

  return [];
}
